import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ChatPageHeader from '@/components/chat/ChatPageHeader';
import ChatSearch from '@/components/chat/ChatSearch';
import ChatFilters from '@/components/chat/ChatFilters';
import ChatTabs from '@/components/chat/ChatTabs';
import LoadingScreen from '@/components/ui/loading-screen';
import { toast } from '@/components/ui/use-toast';
import { filterEnabledModels } from '@/lib/modelSettings';
import { useSupabaseStore } from '@/store/SupabaseStore';
import { useAuth } from '@/contexts/AuthContext';
import { useAppStore } from '@/store';
import Chat from '@/pages/Chat';
import EditorTabBar, { EditorTabItem } from './EditorTabBar';

interface AIChatProps {
  onOpenWindow?: (appId: string, title: string, filePath?: string, content?: string) => void;
  initialChatId?: string;
}

type AITab = {
  id: string;
  kind: 'home' | 'chat';
  title: string;
  path?: string;
  chatId?: string;
};

type AITabState = {
  tabs: AITab[];
  activeId: string;
};

type PersistedAITabState = {
  tabs?: AITab[];
  activeId?: string;
};

const HOME_TAB: AITab = {
  id: 'home',
  kind: 'home',
  title: 'Home',
};

const getChatTabTitle = (title?: string | null) => title?.trim() || 'Untitled Chat';

const createChatTab = (chatId: string, title?: string | null): AITab => ({
  id: chatId,
  kind: 'chat',
  title: getChatTabTitle(title),
  path: `/chat/${chatId}`,
  chatId,
});

const ensureHomeTab = (tabs: AITab[]) => [HOME_TAB, ...tabs.filter((tab) => tab.id !== HOME_TAB.id)];

const AIChat = ({ onOpenWindow, initialChatId }: AIChatProps) => {
  const { chats, refreshChats, addChat, models, chatHasMore, loadMoreChats, fetchModels } = useSupabaseStore();
  const { user } = useAuth();
  const { user: appUser } = useAppStore();
  const [searchTerm, setSearchTerm] = useState('');

  const [filterModels, setFilterModels] = useState<string[]>([]);
  const [showEmpty, setShowEmpty] = useState(true);
  const [isCreatingChat, setIsCreatingChat] = useState(false);
  const [previewsLoading, setPreviewsLoading] = useState(true);
  const [isLoadingChats, setIsLoadingChats] = useState(true);
  const [{ tabs, activeId }, setTabState] = useState<AITabState>({
    tabs: [HOME_TAB],
    activeId: HOME_TAB.id,
  });
  const restoredUserIdRef = useRef<string | null>(null);
  const initialChatOpenedRef = useRef<string | null>(null);
  const visibleModels = useMemo(() => filterEnabledModels(models), [models]);

  useEffect(() => {
    if (models.length === 0) {
      fetchModels().catch(() => undefined);
    }
  }, [fetchModels, models.length]);

  useEffect(() => {
    const visibleModelIds = new Set(visibleModels.map((model) => model.id));
    setFilterModels((current) => current.filter((modelId) => visibleModelIds.has(modelId)));
  }, [visibleModels]);

  useEffect(() => {
    let cancelled = false;

    const loadChats = async () => {
      if (!user) {
        if (!cancelled) {
          setIsLoadingChats(false);
          setPreviewsLoading(false);
        }
        return;
      }

      setIsLoadingChats(true);
      setPreviewsLoading(true);

      try {
        await refreshChats();
      } finally {
        if (!cancelled) {
          setIsLoadingChats(false);
        }
      }
    };

    loadChats();

    return () => {
      cancelled = true;
    };
  }, [refreshChats, user]);

  useEffect(() => {
    if (isLoadingChats) return;

    if (chats.length > 0) {
      const timer = setTimeout(() => {
        setPreviewsLoading(false);
      }, 500);
      return () => clearTimeout(timer);
    }

    setPreviewsLoading(false);
  }, [chats, isLoadingChats]);

  const defaultModel = useMemo(() => {
    if (appUser?.defaultModelApiName) {
      const found = visibleModels.find((model) => model.apiName === appUser.defaultModelApiName);
      if (found) {
        return found;
      }
    }

    const llama3Model = visibleModels.find(
      (model) => model.apiName === 'llama3' || model.name.toLowerCase().includes('llama3')
    );
    if (llama3Model) {
      return llama3Model;
    }

    return visibleModels.find((model) => model.tier === 'free') || visibleModels[0];
  }, [appUser, visibleModels]);

  const filteredChats = useMemo(() => {
    return chats.filter((chat) => {
      if (searchTerm) {
        const titleMatch = chat.title.toLowerCase().includes(searchTerm.toLowerCase());
        const messages = chat.messages || [];
        const contentMatch = messages.some(
          (message) => message.content && message.content.toLowerCase().includes(searchTerm.toLowerCase())
        );

        if (!titleMatch && !contentMatch) {
          return false;
        }
      }

      if (filterModels.length > 0 && !filterModels.includes(chat.modelId)) {
        return false;
      }

      if (!showEmpty && chat.messages && chat.messages.length === 0) {
        return false;
      }

      return true;
    });
  }, [chats, searchTerm, filterModels, showEmpty]);

  const favoriteChats = useMemo(() => {
    return filteredChats.filter((chat) => chat.isFavorite === true);
  }, [filteredChats]);

  const recentChats = useMemo(() => {
    return [...filteredChats].sort(
      (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
    );
  }, [filteredChats]);

  const popularChats = useMemo(() => {
    return [...filteredChats].sort((a, b) => (b.viewCount || 0) - (a.viewCount || 0));
  }, [filteredChats]);

  const chatMap = useMemo(() => {
    return new Map(chats.map((chat) => [chat.id, chat]));
  }, [chats]);

  const storageKey = user ? `ai-chat-tabs:${user.id}` : null;

  useEffect(() => {
    if (!user) {
      restoredUserIdRef.current = null;
      setTabState({ tabs: [HOME_TAB], activeId: HOME_TAB.id });
      return;
    }

    if (isLoadingChats || restoredUserIdRef.current === user.id) {
      return;
    }

    let nextState: AITabState = { tabs: [HOME_TAB], activeId: HOME_TAB.id };

    if (storageKey) {
      try {
        const raw = localStorage.getItem(storageKey);
        if (raw) {
          const parsed = JSON.parse(raw) as PersistedAITabState;
          const restoredTabs = (parsed.tabs || [])
            .filter((tab): tab is AITab => tab.kind === 'chat' && Boolean(tab.chatId))
            .map((tab) => {
              const chat = chatMap.get(tab.chatId!);
              return chat ? createChatTab(chat.id, chat.title) : null;
            })
            .filter((tab): tab is AITab => Boolean(tab));

          const normalizedTabs = ensureHomeTab(restoredTabs);
          const nextActiveId =
            parsed.activeId && normalizedTabs.some((tab) => tab.id === parsed.activeId)
              ? parsed.activeId
              : HOME_TAB.id;

          nextState = {
            tabs: normalizedTabs,
            activeId: nextActiveId,
          };
        }
      } catch (error) {
        console.error('Error restoring AI chat tabs:', error);
      }
    }

    restoredUserIdRef.current = user.id;
    setTabState(nextState);
  }, [chatMap, isLoadingChats, storageKey, user]);

  useEffect(() => {
    if (!user || restoredUserIdRef.current !== user.id || isLoadingChats) {
      return;
    }

    setTabState((previousState) => {
      let changed = false;

      const syncedTabs = ensureHomeTab(
        previousState.tabs.flatMap((tab) => {
          if (tab.kind === 'home') {
            return [HOME_TAB];
          }

          if (!tab.chatId) {
            changed = true;
            return [];
          }

          const chat = chatMap.get(tab.chatId);
          if (!chat) {
            changed = true;
            return [];
          }

          const nextTab = createChatTab(chat.id, chat.title);
          if (tab.title !== nextTab.title || tab.path !== nextTab.path) {
            changed = true;
          }

          return [nextTab];
        })
      );

      const nextActiveId = syncedTabs.some((tab) => tab.id === previousState.activeId)
        ? previousState.activeId
        : HOME_TAB.id;

      if (
        !changed &&
        syncedTabs.length === previousState.tabs.length &&
        nextActiveId === previousState.activeId
      ) {
        return previousState;
      }

      return {
        tabs: syncedTabs,
        activeId: nextActiveId,
      };
    });
  }, [chatMap, isLoadingChats, user]);

  useEffect(() => {
    if (!storageKey || !user || restoredUserIdRef.current !== user.id) {
      return;
    }

    const persistedTabs = tabs.filter((tab) => tab.kind === 'chat');
    const persistedActiveId = tabs.some((tab) => tab.id === activeId) ? activeId : HOME_TAB.id;

    localStorage.setItem(
      storageKey,
      JSON.stringify({
        tabs: persistedTabs,
        activeId: persistedActiveId,
      } satisfies PersistedAITabState)
    );
  }, [activeId, storageKey, tabs, user]);

  const openChatTab = useCallback(
    (chatId: string, title?: string) => {
      setTabState((previousState) => {
        const nextTitle = getChatTabTitle(title || chatMap.get(chatId)?.title);
        const existingTab = previousState.tabs.find((tab) => tab.id === chatId);

        if (existingTab) {
          return {
            tabs: previousState.tabs.map((tab) =>
              tab.id === chatId ? createChatTab(chatId, nextTitle) : tab
            ),
            activeId: chatId,
          };
        }

        return {
          tabs: [...previousState.tabs, createChatTab(chatId, nextTitle)],
          activeId: chatId,
        };
      });
    },
    [chatMap]
  );

  useEffect(() => {
    if (!initialChatId || !user || isLoadingChats) {
      return;
    }

    const chat = chatMap.get(initialChatId);
    if (!chat) {
      return;
    }

    const initialChatKey = `${user.id}:${initialChatId}`;
    if (initialChatOpenedRef.current === initialChatKey) {
      return;
    }

    initialChatOpenedRef.current = initialChatKey;
    openChatTab(chat.id, chat.title);
  }, [chatMap, initialChatId, isLoadingChats, openChatTab, user]);

  const closeTab = useCallback((id: string) => {
    if (id === HOME_TAB.id) {
      return;
    }

    setTabState((previousState) => {
      const tabIndex = previousState.tabs.findIndex((tab) => tab.id === id);
      if (tabIndex === -1) {
        return previousState;
      }

      const nextTabs = ensureHomeTab(previousState.tabs.filter((tab) => tab.id !== id));
      const nextActiveId =
        previousState.activeId === id
          ? previousState.tabs
              .slice(0, tabIndex)
              .reverse()
              .find((tab) => tab.id !== id)?.id || HOME_TAB.id
          : nextTabs.some((tab) => tab.id === previousState.activeId)
            ? previousState.activeId
            : HOME_TAB.id;

      return {
        tabs: nextTabs,
        activeId: nextActiveId,
      };
    });
  }, []);

  const closeOthers = useCallback((id: string) => {
    if (id === HOME_TAB.id) {
      return;
    }

    setTabState((previousState) => {
      const selectedTab = previousState.tabs.find((tab) => tab.id === id);
      if (!selectedTab) {
        return previousState;
      }

      return {
        tabs: ensureHomeTab([selectedTab]),
        activeId: selectedTab.id,
      };
    });
  }, []);

  const closeRight = useCallback((id: string) => {
    if (id === HOME_TAB.id) {
      return;
    }

    setTabState((previousState) => {
      const selectedIndex = previousState.tabs.findIndex((tab) => tab.id === id);
      if (selectedIndex === -1) {
        return previousState;
      }

      const nextTabs = ensureHomeTab(
        previousState.tabs.filter((tab, index) => tab.id === HOME_TAB.id || index <= selectedIndex)
      );
      const nextActiveId = nextTabs.some((tab) => tab.id === previousState.activeId)
        ? previousState.activeId
        : id;

      return {
        tabs: nextTabs,
        activeId: nextActiveId,
      };
    });
  }, []);

  const reorderTabs = useCallback((orderedIds: string[]) => {
    setTabState((previousState) => {
      const chatTabs = previousState.tabs.filter((tab) => tab.kind === 'chat');
      const chatTabMap = new Map(chatTabs.map((tab) => [tab.id, tab]));
      const orderedChatTabs = orderedIds
        .filter((id) => id !== HOME_TAB.id)
        .map((id) => chatTabMap.get(id))
        .filter((tab): tab is AITab => Boolean(tab));
      const remainingTabs = chatTabs.filter(
        (tab) => !orderedChatTabs.some((orderedTab) => orderedTab.id === tab.id)
      );

      return {
        ...previousState,
        tabs: [HOME_TAB, ...orderedChatTabs, ...remainingTabs],
      };
    });
  }, []);

  const popOutTab = useCallback(
    (id: string) => {
      const tab = tabs.find((candidate) => candidate.id === id && candidate.kind === 'chat');
      if (!tab?.chatId) {
        return;
      }

      onOpenWindow?.('ai-chat', `Chat - ${tab.title}`, tab.chatId);
    },
    [onOpenWindow, tabs]
  );

  const handleOpenChat = useCallback(
    (id: string, title?: string) => {
      openChatTab(id, title);
    },
    [openChatTab]
  );

  const handleNewChat = useCallback(async () => {
    if (!user || !defaultModel || isCreatingChat) {
      if (!defaultModel) {
        toast({
          title: 'No AI Model Available',
          description: 'Cannot create a new chat because no AI models are configured.',
          variant: 'destructive',
        });
      }
      return;
    }

    setIsCreatingChat(true);

    try {
      const newChat = await addChat(user.id, 'New Chat', defaultModel.id);
      if (!newChat?.id) {
        throw new Error('Failed to create chat');
      }

      openChatTab(newChat.id, newChat.title);
    } catch (error) {
      console.error('Error creating new chat:', error);
      toast({
        title: 'Error',
        description: 'Failed to create a new chat. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsCreatingChat(false);
    }
  }, [addChat, defaultModel, isCreatingChat, openChatTab, user]);

  const handleLoadMore = useCallback(async () => {
    if (!chatHasMore) return;

    setPreviewsLoading(true);
    try {
      await loadMoreChats();
    } catch (error) {
      console.error('Error loading more chats:', error);
    } finally {
      setPreviewsLoading(false);
    }
  }, [chatHasMore, loadMoreChats]);

  const editorTabs = useMemo<EditorTabItem[]>(() => {
    return tabs.map((tab) => ({
      id: tab.id,
      name: tab.title,
      path: tab.path || '',
      isPinned: tab.kind === 'home',
    }));
  }, [tabs]);

  const activeTab = useMemo(() => {
    return tabs.find((tab) => tab.id === activeId) || HOME_TAB;
  }, [activeId, tabs]);

  if (!user) {
    return (
      <div className="flex h-full items-center justify-center bg-background p-6">
        <p className="text-sm text-muted-foreground">Sign in to view your chats in MapleOS.</p>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col bg-background">
      <div className="pt-[5px]">
        <EditorTabBar
          tabs={editorTabs}
          activeId={activeId}
          onSelect={(id) => setTabState((previousState) => ({ ...previousState, activeId: id }))}
          onClose={closeTab}
          onReorder={reorderTabs}
          onNewUntitled={handleNewChat}
          onCloseOthers={closeOthers}
          onCloseRight={closeRight}
          onSave={() => undefined}
          onSaveAll={() => undefined}
          onRevealInExplorer={() => undefined}
          onCopyPath={() => undefined}
          onPopOut={popOutTab}
          showSaveItems={false}
          showRevealItems={false}
        />
      </div>

      <div className="min-h-0 flex-1">

        {activeTab.kind === 'home' ? (
          <div className="h-full overflow-y-auto">
            <div className="mx-auto flex min-h-full max-w-7xl flex-col gap-4 p-6">
              <ChatPageHeader onNewChat={handleNewChat} />

              <div className="flex flex-col items-center gap-4 md:flex-row">
                <ChatSearch searchTerm={searchTerm} setSearchTerm={setSearchTerm} />
                <ChatFilters
                  showEmpty={showEmpty}
                  setShowEmpty={setShowEmpty}
                  filterModels={filterModels}
                  setFilterModels={setFilterModels}
                  models={visibleModels}
                />
              </div>

              {isLoadingChats ? (
                <div className="flex flex-1 items-center justify-center py-12">
                  <LoadingScreen />
                </div>
              ) : (
                <ChatTabs
                  recentChats={recentChats}
                  favoriteChats={favoriteChats}
                  popularChats={popularChats}
                  handleNewChat={handleNewChat}
                  loadingMessages={previewsLoading}
                  onLoadMore={handleLoadMore}
                  hasMore={chatHasMore}
                  onOpenChat={handleOpenChat}
                />
              )}
            </div>
          </div>
        ) : (
          <div className="h-full">
            <Chat
              key={activeTab.id}
              chatIdOverride={activeTab.chatId}
              embeddedInMapleOS
              onInvalidChat={() => closeTab(activeTab.id)}
            />
          </div>
        )}

      </div>
    </div>
  );
};

export default AIChat;